/**
 * What this build ships.
 *
 * The GoCaddy contract is a MAP product: distances, the hole map, cart paths,
 * the parked buggy. The client keeps scoring in the app they already use, so
 * scoring and handicap posting are off by default here.
 *
 * They are flags rather than deletions on purpose. The scorecard, the stableford
 * and match engines, the handicap allocation and the GOLF Link / DotGolf / GHIN
 * posting are all built and tested. Ripping them out would destroy working code
 * to satisfy a scope decision that may well reverse — "can we add scoring after
 * all" is a common second conversation. Behind a flag, that is one word.
 *
 * Turn a feature on for a build:
 *   VITE_FEATURE_SCORING=on npm run build
 */
const on = (v: string | undefined, dflt: boolean) =>
  v == null ? dflt : v === 'on' || v === 'true' || v === '1';

// import.meta.env only exists under Vite. Reading it directly makes this module
// unimportable from a plain node test, and the scope of what we ship is exactly
// the thing that deserves a test.
const env: Record<string, string | undefined> =
  (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};

export const FEATURES = {
  /** Scorecard, stableford/match play, pace of play. */
  scoring: on(env.VITE_FEATURE_SCORING, false),
  /** Posting rounds to a national handicap body. Meaningless without scoring. */
  federation: on(env.VITE_FEATURE_FEDERATION, false),
  /**
   * Live fleet tracking + the pro shop view.
   *
   * 🟥 OFF: the client sells carts to individuals who own ONE each. There is no
   * fleet. This was built for a course-operator model that does not apply, and
   * a "pro shop" tab in front of a private cart owner is confusing, not extra.
   * Flagged rather than deleted — a course customer would want it back.
   */
  fleet: on(env.VITE_FEATURE_FLEET, false),
  /** Capture a course's holes on the ground. Operator feature. */
  survey: on(env.VITE_FEATURE_SURVEY, true),
  /**
   * Elevation relief under the hole view. On by default: this is the map
   * product's headline, not an extra. Off is the fallback for a head unit whose
   * GPU cannot carry it — see docs/MERCURY_FAIRWAY_TERRAIN.md.
   */
  terrain: on(env.VITE_FEATURE_TERRAIN, true),
} as const;

/**
 * Stroke index only matters for handicap scoring. With scoring off, a course
 * missing it is perfectly usable — which removes the single biggest data
 * blocker to rolling out all 1,449 Australian courses.
 */
export const needsStrokeIndex = () => FEATURES.scoring;

export type TabId = 'map' | 'score' | 'post' | 'courses' | 'fleet' | 'survey' | 'setup' | 'widget';

/**
 * Which tabs this build shows. Extracted from the shell so the scope decision
 * is testable: "the app we sell has no scoring in it" should be an assertion,
 * not something someone eyeballs before a demo.
 */
export function visibleTabs(
  role: 'buggy' | 'shop',
  features: { scoring: boolean; federation: boolean; fleet: boolean; survey: boolean } = FEATURES
): TabId[] {
  if (role === 'shop') {
    return [
      features.fleet && 'fleet',
      features.survey && 'survey',
      'setup',
    ].filter(Boolean) as TabId[];
  }
  return [
    'map',
    features.scoring && 'score',
    features.scoring && features.federation && 'post',
    'courses',
    features.fleet && 'fleet',
    'setup',
  ].filter(Boolean) as TabId[];
}
